import { artworkRequired, isArtworkDelivered } from "./chunk-2A65ZKZ4.js";
import { getProductVariantVersion } from "./chunk-KFHYJPNW.js";
import { SERVICES_WITHOUT_USER_ARTWORK, hasActiveArtworkServiceOfTypes } from "./chunk-JZDFGTTT.js";
import { LINE_ITEM_STATES, getLineItemStateIdByName, getStateNameById } from "./chunk-7FPOUA55.js";

// src/orchestrations.ts
var hasOrchestration = lineItem => {
  var _a;
  return Boolean(((_a = lineItem.custom) == null ? void 0 : _a.fields.orchestrationId) && lineItem.custom.fields.serviceGroup);
};
var isOrchestrationLineItemByRecipient = (lineItem, recipient) => {
  var _a;
  return Boolean(hasOrchestration(lineItem) && ((_a = lineItem.custom) == null ? void 0 : _a.fields.recipient) === recipient);
};
var getItemOrchestration = lineItem => {
  var _a;
  if (((_a = lineItem.custom) == null ? void 0 : _a.fields.orchestration) === void 0) {
    throw new Error("No orchestration found on line item ".concat(lineItem.id));
  }
  return JSON.parse(lineItem.custom.fields.orchestration);
};
var getServiceGroups = lineItems => {
  const orchestratedItems = lineItems.filter(hasOrchestration);
  return orchestratedItems.reduce((groups, lineItem) => {
    var _a;
    const serviceGroup = (_a = lineItem.custom) == null ? void 0 : _a.fields.serviceGroup;
    if (serviceGroup) {
      if (groups[serviceGroup]) {
        groups[serviceGroup].push(lineItem);
      } else {
        groups[serviceGroup] = [lineItem];
      }
    }
    return groups;
  }, {});
};
var getDependencyInformation = orderOrCart => {
  var _a;
  try {
    const dependencyInformation = JSON.parse((_a = orderOrCart.custom) == null ? void 0 : _a.fields.dependencyInformation);
    return dependencyInformation;
  } catch (error) {
    return void 0;
  }
};
var isDependencyFlowStartItem = (lineItem, dependencyInformation) => {
  var _a, _b;
  const orchestrationserviceGroup = (_a = lineItem.custom) == null ? void 0 : _a.fields.serviceGroup;
  if (!orchestrationserviceGroup) {
    return false;
  }
  const dependencyFlowGroup = dependencyInformation == null ? void 0 : dependencyInformation.dependencyFlow[orchestrationserviceGroup];
  if (!dependencyFlowGroup) {
    return false;
  }
  const startItem = dependencyFlowGroup.find(dependencyFlowItem => dependencyFlowItem.priority === 0);
  return (startItem == null ? void 0 : startItem.itemKey) === ((_b = lineItem.custom) == null ? void 0 : _b.fields.productKey);
};
var getDependencyFlowStartItem = (lineItems, dependencyInformation) => {
  const startItem = lineItems.find(lineItem => isDependencyFlowStartItem(lineItem, dependencyInformation));
  return startItem;
};
var getRequisitesLineItemIds = (lineItem, dependencyInformation) => {
  var _a;
  const lineItemId = lineItem.id;
  const serviceGroup = (_a = lineItem.custom) == null ? void 0 : _a.fields.serviceGroup;
  const requisitesItemsId = [];
  dependencyInformation.dependencyFlow[serviceGroup].forEach(dependencyFlowItem => {
    if (dependencyFlowItem.requisites.length === 0) {
      return;
    }
    dependencyFlowItem.requisites.forEach(requisite => {
      if (requisite.lineItemId === lineItemId && dependencyFlowItem.lineItemId) {
        requisitesItemsId.push(dependencyFlowItem.lineItemId);
      }
    });
  });
  return requisitesItemsId;
};
var getDependencyFlowStartItemState = (lineItems, dependencyInformation, environment) => {
  var _a;
  const startItem = getDependencyFlowStartItem(lineItems, dependencyInformation);
  return (_a = startItem == null ? void 0 : startItem.state[0].state.id) != null ? _a : getLineItemStateIdByName(environment, "Processing");
};
var getDependencyFlowState = function (lineItems, dependencyInformation, environment) {
  let fallbackState = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "ReadyForFulfillment";
  var _a;
  const postInjectionStates = ["SentToMCP", "PrintingInProgress", "FulfillmentRejected", "Shipped"];
  if (lineItems.length === 0 || !dependencyInformation) {
    return getLineItemStateIdByName(environment, fallbackState);
  }
  const startItemStateId = getDependencyFlowStartItemState(lineItems, dependencyInformation, environment);
  const startItemState = getStateNameById(environment, startItemStateId);
  const allStatesTheSame = lineItems.every(lineItem => lineItem.state[0].state.id === lineItems[0].state[0].state.id);
  if (allStatesTheSame) {
    return startItemStateId;
  }
  if (!postInjectionStates.includes(startItemState)) {
    return startItemStateId;
  }
  if (postInjectionStates.includes(startItemState) && startItemState !== "Shipped") {
    return getLineItemStateIdByName(environment, "ReadyForFulfillment");
  }
  const serviceGroup = (_a = lineItems[0].custom) == null ? void 0 : _a.fields.serviceGroup;
  const dependencyFlowGroup = dependencyInformation.dependencyFlow[serviceGroup];
  const startItem = dependencyFlowGroup == null ? void 0 : dependencyFlowGroup.find(dependencyFlowItem => dependencyFlowItem.priority === 0);
  const shippedTriggers = startItem == null ? void 0 : startItem.triggers.Shipped;
  const nextItemKey = shippedTriggers == null ? void 0 : shippedTriggers[0].itemKey;
  const nextItem = lineItems.find(lineItem => {
    var _a2, _b;
    return ((_a2 = lineItem.custom) == null ? void 0 : _a2.fields.serviceKey) === nextItemKey || ((_b = lineItem.custom) == null ? void 0 : _b.fields.productKey) === nextItemKey;
  });
  if (startItemState === "Shipped" && nextItem) {
    return nextItem.state[0].state.id;
  }
  return getLineItemStateIdByName(environment, fallbackState);
};
var getOrchestrationItemById = (lineItems, id) => {
  const item = lineItems.find(lineItem => {
    const orchestration = getItemOrchestration(lineItem);
    return orchestration.id === id;
  });
  if (!item) {
    throw new Error("No orchestration item found with id ".concat(id));
  }
  return item;
};
var getOrchestrationItemByRecipient = (lineItems, recipient) => {
  const item = lineItems.find(lineItem => isOrchestrationLineItemByRecipient(lineItem, recipient));
  if (!item) {
    throw new Error("No orchestration item found with recipient ".concat(recipient));
  }
  return item;
};

// src/util.ts
function includes(coll, el) {
  return coll.includes(el);
}

// src/lineItem.ts
var getAttributeDescription = (lineItem, locale) => {
  if (!lineItem.custom) return null;
  const {
    isReviewGroupLead,
    productDescription,
    reviewGroupLeadProductDescription,
    userSelection
  } = lineItem.custom.fields;
  const descriptions = isReviewGroupLead && reviewGroupLeadProductDescription ? reviewGroupLeadProductDescription : productDescription;
  if (descriptions && descriptions[locale]) {
    return descriptions[locale];
  }
  return userSelection == null ? void 0 : userSelection.map(selection => {
    const [attribute, ...value] = selection.split(":");
    return "".concat(attribute, ": ").concat(value.join(":"));
  }).join(", ");
};
var getCategoryName = (lineItem, locale) => {
  var _a;
  if ((_a = lineItem.custom) == null ? void 0 : _a.fields) {
    const {
      localizedProductName,
      merchandisingProductName
    } = lineItem.custom.fields;
    if (localizedProductName && localizedProductName[locale]) {
      return localizedProductName[locale];
    }
    const splitLocale = locale.split("-");
    return localizedProductName && localizedProductName[splitLocale[0]] ? localizedProductName[splitLocale[0]] : merchandisingProductName;
  }
  return null;
};
var getLineItemDescription = (lineItem, locale) => "".concat(getCategoryName(lineItem, locale), ", ").concat(getAttributeDescription(lineItem, locale));
var getLineItemStateName = (lineItem, env) => {
  const stateId = lineItem.state[0].state.id;
  const name = getStateNameById(env, stateId);
  return name;
};
var isLineItemCancellable = (lineItem, env) => {
  const stateName = getLineItemStateName(lineItem, env);
  const ALLOWED_LINE_ITEM_STATES = [LINE_ITEM_STATES.INITIAL, LINE_ITEM_STATES.PROCESSING, LINE_ITEM_STATES.CONFIRMED, LINE_ITEM_STATES.HELD, LINE_ITEM_STATES.WAITING_FOR_ARTWORK, LINE_ITEM_STATES.WAITING_FOR_DOC_REVIEW, LINE_ITEM_STATES.WAITING_FOR_SECONDARY_DOC_REVIEW, LINE_ITEM_STATES.DOC_REVIEW_FAILED, LINE_ITEM_STATES.DOC_REVIEW_REJECTED, LINE_ITEM_STATES.PROCESSING_CLOUDLAB_ARTWORK_FAILED, LINE_ITEM_STATES.PROCESSING_CLOUDLAB_ARTWORK, LINE_ITEM_STATES.DOC_REVIEW_PROOFING, LINE_ITEM_STATES.WAITING_FOR_PROPOSAL];
  return includes(ALLOWED_LINE_ITEM_STATES, stateName);
};
var isLineItemCancelled = (lineItem, env) => {
  const stateName = getLineItemStateName(lineItem, env);
  if (!stateName) return false;
  const ALLOWED_LINE_ITEM_STATES = [LINE_ITEM_STATES.CANCELLED];
  return includes(ALLOWED_LINE_ITEM_STATES, stateName);
};
var isGroupLineItem = lineItem => {
  if (!lineItem.custom) return false;
  const {
    reviewGroup,
    fulfillmentGroup,
    multipleDesignsGroup
  } = lineItem.custom.fields;
  return Boolean(reviewGroup || fulfillmentGroup || multipleDesignsGroup);
};
var hasSplitShipment = lineItem => {
  var _a;
  const shippingAddressPerLineItem = (_a = lineItem.custom) == null ? void 0 : _a.fields.shippingAddressPerLineItem;
  return Boolean(shippingAddressPerLineItem && shippingAddressPerLineItem.length > 1);
};
var isQuoteLineItem = lineItem => {
  var _a;
  const quoteId = (_a = lineItem.custom) == null ? void 0 : _a.fields.quoteId;
  return Boolean(quoteId);
};
var hasPickupPoint = lineItem => {
  var _a;
  return Boolean((_a = lineItem.custom) == null ? void 0 : _a.fields.isWithPickupLocation);
};
var getPickupPointHref = lineItem => {
  var _a;
  const pickupPointField = (_a = lineItem.custom) == null ? void 0 : _a.fields.pickupPoint;
  let href;
  if (pickupPointField) {
    try {
      href = JSON.parse(pickupPointField)[0].href;
    } catch (error) {}
  }
  return href;
};
var areSomeLineItemsCancelled = (lineItems, env) => lineItems.some(lineItem => getLineItemStateName(lineItem, env) === LINE_ITEM_STATES.CANCELLED);
var isLineItemEditable = (env, lineItem, lineItems) => {
  const stateName = getLineItemStateName(lineItem, env);
  const someCancelled = areSomeLineItemsCancelled(lineItems, env);
  const isGrouped = isGroupLineItem(lineItem);
  const isSplitShipped = hasSplitShipment(lineItem);
  const isQuote = isQuoteLineItem(lineItem);
  const isPickupPoint = hasPickupPoint(lineItem);
  const isOrchestrated = hasOrchestration(lineItem);
  if (someCancelled || isGrouped || isSplitShipped || isQuote || isPickupPoint || isOrchestrated) {
    return false;
  }
  const disallowedStates = [LINE_ITEM_STATES.FULFILLMENT_REJECTED, LINE_ITEM_STATES.FULFILLMENT_ACCEPTED, LINE_ITEM_STATES.FULFILLMENT_ERROR, LINE_ITEM_STATES.PRINTING_IN_PROGRESS, LINE_ITEM_STATES.DOC_REVIEW_IN_PROGRESS, LINE_ITEM_STATES.WAITING_FOR_DOC_REVIEW, LINE_ITEM_STATES.WAITING_FOR_SECONDARY_DOC_REVIEW, LINE_ITEM_STATES.SHIPPED, LINE_ITEM_STATES.CANCELLED, LINE_ITEM_STATES.SENT_TO_MCP, LINE_ITEM_STATES.HELD, LINE_ITEM_STATES.PROCESSING_CLOUDLAB_ARTWORK];
  return !includes(disallowedStates, stateName);
};
var isReorderable = function (lineItem) {
  let checkProductVersion = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let reOrderableQuote = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (reOrderableQuote) {
    return true;
  }
  if (isQuoteLineItem(lineItem) || hasPickupPoint(lineItem)) {
    return false;
  }
  if (!checkProductVersion) {
    return true;
  }
  return getProductVariantVersion(lineItem.variant) !== "1";
};
var canChangeShippingAddress = (env, lineItem) => {
  const stateName = getLineItemStateName(lineItem, env);
  const disallowedStates = [LINE_ITEM_STATES.CANCELLED, LINE_ITEM_STATES.SHIPPED];
  return !includes(disallowedStates, stateName);
};
var isUserArtworkNeeded = (orderOrCart, lineItem, env) => artworkRequired(lineItem) && !isArtworkDelivered(lineItem) && !isLineItemCancelled(lineItem, env) && !hasActiveArtworkServiceOfTypes(lineItem.id, orderOrCart, SERVICES_WITHOUT_USER_ARTWORK, env);
var isAnyUserArtworkNeeded = (orderOrCart, env) => orderOrCart.lineItems.some(lineItem => isUserArtworkNeeded(orderOrCart, lineItem, env));
var getAssociatedGroupedLineItems = (orderOrCart, lineItem, settings) => {
  const {
    reviewGroup: lineItemReviewGroup,
    fulfillmentGroup: lineItemFulfillmentGroup,
    multipleDesignsGroup: lineItemMultipleDesignsGroup,
    serviceGroup: lineItemServiceGroup
  } = lineItem.custom.fields;
  const checkReviewGroup = lineItemReviewGroup && (settings ? settings.reviewGroup : true);
  const checkFulfillmentGroup = lineItemFulfillmentGroup && (settings ? settings.fulfillmentGroup : true);
  const checkMultipleDesignsGroup = lineItemMultipleDesignsGroup && (settings ? settings.multipleDesignsGroup : true);
  const checkServiceGroup = lineItemServiceGroup && (settings ? settings.serviceGroup : true);
  return orderOrCart.lineItems.filter(orderOrCartLineItem => {
    if (orderOrCartLineItem.id === lineItem.id) {
      return false;
    }
    if (checkReviewGroup && orderOrCartLineItem.custom.fields.reviewGroup === lineItemReviewGroup) {
      return true;
    }
    if (checkFulfillmentGroup && orderOrCartLineItem.custom.fields.fulfillmentGroup === lineItemFulfillmentGroup) {
      return true;
    }
    if (checkMultipleDesignsGroup && orderOrCartLineItem.custom.fields.multipleDesignsGroup === lineItemMultipleDesignsGroup) {
      return true;
    }
    if (checkServiceGroup && orderOrCartLineItem.custom.fields.serviceGroup === lineItemServiceGroup) {
      return true;
    }
    return false;
  });
};
export { hasOrchestration, isOrchestrationLineItemByRecipient, getItemOrchestration, getServiceGroups, getDependencyInformation, isDependencyFlowStartItem, getDependencyFlowStartItem, getRequisitesLineItemIds, getDependencyFlowStartItemState, getDependencyFlowState, getOrchestrationItemById, getOrchestrationItemByRecipient, includes, getAttributeDescription, getCategoryName, getLineItemDescription, getLineItemStateName, isLineItemCancellable, isLineItemCancelled, isGroupLineItem, hasSplitShipment, isQuoteLineItem, hasPickupPoint, getPickupPointHref, areSomeLineItemsCancelled, isLineItemEditable, isReorderable, canChangeShippingAddress, isUserArtworkNeeded, isAnyUserArtworkNeeded, getAssociatedGroupedLineItems };